

















































import { Vue } from "vue-property-decorator";
import browserDetect from "vue-browser-detect-plugin";

import voiceSearchBtn from "@/Webchat/components/voiceSearchBtn.vue";
import chatInput from "@/Webchat/components/chatInput.vue";
import chatHeader from "@/Webchat/components/chatHeader.vue";
import chatBody from "@/Webchat/components/chatBody.vue";
import selectLang from "@/Webchat/components/selectLang.vue";
import captureLead from "@/Webchat/components/captureLead.vue";
import PoweredBy from '@/Webchat/components/poweredBy.vue';
import ErrorState from "@/Webchat/components/errorState.vue";

import soundNotification from "@/mixins/soundNotification";

import { WebchatClient, WebchatMessage } from "@/libs/WebchatClient";
import { applySettingsTheme } from "@/libs/theme";
import { PreferenceSettings, WebchatSettings, ProfileLangs, WebchatUser, ProfileWebchatSettings } from "@/libs/WebchatSettings";
import { message, IMessage, QuickReply } from "@/utilities/index";

declare global {
  interface Window {
    browserDetect: any;
  }
}
Vue.use(browserDetect);


const webChat = Vue.extend({
  name: "WebChat",
  mixins: [soundNotification],
  components: {
    chatHeader,
    chatBody,
    voiceSearchBtn,
    captureLead,
    selectLang,
    PoweredBy,
    chatInput,
    ErrorState
  },
  data() {
    return {
      isFullScreen: false,
      messages: [],
      disconnected: false,
      webError: false,
      getLastTenMessages: false,
      lastError: null,
      voices: [],
      voiceMode: false,
      profileDefaultLang: null,
      lastMsg: null,
      continuous: false,
      supported: false,
      state: 'online',
      profileLanguageSettings: null,
      profileSupportedLanguages: null,
      loadingData: true,
      chatHeaderTxt: '',
      agentImg: '',
      agentIdentity: 'Bot',
      loadLanguageSelector: false,
      enableWebviewCaptureLead: false,
      langs: null,
      loadCaptureLeadWebview: false,
      hideMic: false,
      userPreferredLang: null,
      openSelectLang: false,
      addBackBtn: false,
      attachmentsFiles: [],
      closeSelectLang: false,
      selectedLang: null,
      initMessage: '',
      showTypingIndicator: false,
      showErrorState: false,
    };
  },
  async created() {
    console.log("creating webchat");
    window.botClient = new WebchatClient(this);
    this.$on("message", this.onMessage);
    this.$on("isFullScreenMode", this.toggleFullScreenMode);
    this.$on("triggerMessage", this.triggerMessage);
    this.$on("disconnected", this.disconnectedStatus);
    this.$on("connected", this.connectedStatus);
    this.$on("webhook_error", this.webhookError);
    this.$on("history_loaded", this.historyLoaded);
    this.$on("empty_history", this.emptyHistory);
    this.$on("settings_loaded", this.onSettingsLoaded);
    this.$on("init_message", this.updateInitMessage);
    this.$on("webchat_settings_loaded", this.onWebchatSettingsLoaded);
    this.$on("webchat_settings_error", this.onWebchatSettingsError);
    this.$on("supported_languages_loaded", this.onProfileSupportedLanguages);
    this.$on("language_settings_loaded", this.onLanguageSettingsLoaded);
    this.$on("agents_loaded", this.getProfileAgents);
    this.$on("langs_loaded", this.getProfilelangs);
    this.$on("enableWebview", this.isCaptureLeadEnabled);
    this.$on("loadUserLanguageSelector", this.isLanguageSelectorEnabled);
    this.$on("activateMobileTheme", this.manageMobileTheme);
    this.$on("activateStandaloneMode", this.manageStandaloneMode);
    this.$on("onToggleBubble", this.onToggleBubble);
    this.$on("lang_updated", this.updateLang);
    this.$on("setup_error", this.onSetupError);
    this.$on("setup_success", this.onSetupSuccess);
    this.$on("data_loaded", this.onDataLoaded);

    this.$on("typing", (val: boolean) => {
      this.showTypingIndicator = val;
    });
  },
  computed: {
    closeButton() {
      return this.$vstore.settings.state.preferenceSettings.closeButton;
    },
    profileId() {
      return this.$vstore.settings.state.preferenceSettings.profileId;
    },
    channelId() {
      return this.$vstore.settings.state.preferenceSettings.channelId;
    },
    token() {
      return this.$vstore.settings.state.preferenceSettings.token;
    },
    profileName() {
      return this.$vstore.settings.state.preferenceSettings.profile;
    },
    showLanugageSelector() {
      return this.openSelectLang || this.loadLanguageSelector;
    },
    supportedLanguages() {
      return this.profileSupportedLanguages?.languages?.map(lang => lang.key) ?? [];
    },
    webchatSettings() {
      return this.$vstore.settings.state.channelSettings ?? {};
    }
  },
  mounted() {
    window.botClient.start();
    if (
      this.$router &&
      this.$router.history.current &&
      this.$router.history.current.params.isBack
    ) {
      window.botClient.notifyParent({ name: "openIframe" });
    }
  },
  methods: {
    handleErrorRetry() {
      location.reload();
    },
    onSetupError() {
      this.showErrorState = true;
      this.loadingData = false;
    },
    onSetupSuccess() {
      this.showErrorState = false;
    },
    onDataLoaded() {
      this.loadingData = false;
    },
    updateInitMessage(message: string) {
      this.initMessage = message;
      window.botClient.notifyParent({ name: "openIframe" });
    },
    closeSelectLangToggle() {
      this.closeSelectLang = false;
    },
    triggerMessage(text: string) {
      let message: IMessage = {};
      message.text = this.cleanText(text);
      //this message comes from trigger rules
      message.to = true;
      this.onMessage(message);
      window.botClient.notifyParent({ name: "openIframe" });
    },
    formatReceiveMessage(msg: IMessage, isHistory?: boolean) {
      message.formatReceiveMessage(msg);
      const operatorImage = this.$vstore.settings.state.channelSettings.operatorImage;
      msg.sender_picture = (msg.source?.toLowerCase() === 'bot' ? operatorImage : message.getAgentImage(msg, isHistory));
    },
    onMessage(msg: IMessage) {
      this.formatReceiveMessage(msg);
      this.getSenderIdentity(msg);
      this.clearQuickReplies(msg);
      this.handleVoiceMode(msg);
      let count: number = message.getUnreadCount(this.messages);
      this.notifyCount(count);
      this.playSound();
    },
    handleVoiceMode(message: IMessage) {
      if (this.voiceMode && message.audioContent) {
        this.updateLastMsg(message.audioContent);
      }
    },
    updateLastMsg(lastMsg: string) {
      this.lastMsg = lastMsg;
    },
    cleanText(text: string) {
      return message.cleanText(text);
    },
    formatTextMessage(text: string) {
      if (!text && this.$vstore.Attachments.state.attachmentsReadyToSend.length <= 0) return;
      let msg = message.formatTextMessage(text, this.$vstore.Attachments.state.attachmentsReadyToSend);
      this.sendMessage(msg);
    },
    constructTactfulAttachments() {
      let attachments = [];
      if (this.$vstore.Attachments.state.attachmentsReadyToSend && this.$vstore.Attachments.state.attachmentsReadyToSend.length > 0) {
        for (let index in this.$vstore.Attachments.state.attachmentsReadyToSend) {
          let attach = message.constructTactfulAttachments(this.$vstore.Attachments.state.attachmentsReadyToSend[index]);
          attachments.push(attach);
        }
      }
      return attachments;
    },
    sendMessage(msg: IMessage) {
      let sendMessage = message.formatSendMessage(msg, this.constructTactfulAttachments());
      this.clearQuickReplies(sendMessage);
      window.botClient.sendMessage(sendMessage);
      message.setunreadMessagesTimestamp(sendMessage.timestamp);
      this.resetUnreadCount();
      this.resetAttachments();
    },
    resetAttachments() {
      this.$vstore.Attachments.state.attachmentsReadyToSend = [];
      this.attachmentsFiles = [];
    },
    sendQuickReply(quickReply: QuickReply) {
      this.sendMessage({
        text: quickReply.title,
        quick_reply: { payload: quickReply.payload }
      });
    },
    openWebexWebview(payload: any) {
      let parsedPayload = payload;
      if (message.checkWebexPostback(parsedPayload)) {
        this.$router.push({
          name: 'webex',
          params: {
            department: parsedPayload.department,
            mail: parsedPayload.mail,
            source: 'webChat',
            pageName: parsedPayload.pageName,
            profileName: this.profileName,
            chatHeaderColor: this.webchatSettings.chatHeaderColor,
            chatHeaderTxtColor: this.webchatSettings.chatHeaderTxtColor,
            whiteLabel: this.webchatSettings.enableWhiteLabel
          }
        });
      }
    },
    handlePostback(payload: any, title: string) {
      this.openWebexWebview(payload);
      this.sendMessage({
        text: title,
        postback: {
          payload: payload
        }
      });
    },
    clearQuickReplies(msg: WebchatMessage) {
      this.messages = message.clearQuickReplies(msg, this.messages, this.getLastTenMessages);
    },
    closeChatWindow() {
      window.botClient.notifyParent({ name: "closeIframe" });
      this.isFullScreen = false;
    },
    toggleFullScreenMode(isFullScreen: boolean) {
      this.isFullScreen = isFullScreen;
    },
    async onSettingsLoaded(profileSettings: ProfileWebchatSettings) {
      this.$vstore.settings.setProfileSettings(profileSettings);
    },
    disconnectedStatus() {
      this.disconnected = true;
    },
    connectedStatus(preferenceSettings: PreferenceSettings) {
      this.$vstore.settings.setPreferenceSettings(preferenceSettings);
      this.voiceMode = preferenceSettings.voiceMode;
      this.profileDefaultLang = preferenceSettings.lang;
      this.getLastTenMessages = preferenceSettings.getLastTenMessages;
      this.continuous = preferenceSettings.continuous;
      this.disconnected = false;
      if (!preferenceSettings.closeButton) {
        this.isFullScreen = true;
      }
      if (preferenceSettings.currentSession.url.includes('https://store.demo.tactful.ai/')) {
        this.hideMic = true;
      }
    },
    webhookError() {
      this.webError = false;
    },

    onError(err: Error) {
      this.clear();
      this.lastError = err.message;
    },
    clear() {
      this.lastError = null;
    },
    //load all history in messages to display
    historyLoaded(historyMessages: any) {
      this.messages = [];   // empty any previous history displayed from a previous session (Example: chatting as anynomous then logging in)
      if (historyMessages) {
        let formattedHistory = historyMessages.map((msg: any) => {
          let newMessage = Object.assign(msg, msg.message);
          this.formatReceiveMessage(newMessage, true);
          return newMessage;
        });

        // check agent name for last message from bot/ inbox source
        const filteredMessages = formattedHistory.filter(message => message.source === 'bot' || message.source === 'inbox');
        if (filteredMessages.length >= 1) {
          this.getSenderIdentity(filteredMessages[filteredMessages.length - 1], true);
        }
        this.messages.unshift(...formattedHistory);
        let count: number = message.getUnreadCount(this.messages);
        this.notifyCount(count);
      }
    },
    emptyHistory() {
      console.log("You have no history messages.");
      this.messages = [];
    },
    togglevoiceMode(toggle: boolean) {
      this.voiceMode = toggle;
    },
    activateTypingMood() {
      this.voiceMode = false;
    },
    isSupported() {
      let isChrome = this.$browserDetect.isChrome || (navigator && navigator.userAgent && navigator.userAgent.match('CriOS'));
      let isSupported = (!isChrome && this.voiceMode) ? false : true;
      return isSupported;
    },
    onProfileSupportedLanguages({ data }) {
      this.profileSupportedLanguages = data ?? {};
    },
    onLanguageSettingsLoaded({ data }) {
      this.profileLanguageSettings = data;
      // load locale
      if (this.userPreferredLang) {
        this.setLocale(this.userPreferredLang);
      } else if (data?.language) { // default language 
        this.setLocale(data.language);
      }
    },
    onWebchatSettingsError(error: Error) {
      console.error("Error loading webchat settings:", error);
    },
    onWebchatSettingsLoaded(webchatSettings: WebchatSettings) {
      this.$vstore.settings.setWebchatSettings(webchatSettings);
      this.enableWebviewCaptureLead = webchatSettings.enableWebviewCaptureLead;
      this.chatHeaderTxt = webchatSettings.chatHeaderTxt;
      this.agentImg = webchatSettings.operatorImage;
      applySettingsTheme(webchatSettings, document.body);
    },
    getSenderIdentity(msg: IMessage, isHistory?: boolean) {
      // check if msg was from bot or agent >> DEFINE it in msg      
      this.agentIdentity = message.getSenderIdentity(msg);
      const isBot = this.agentIdentity == 'Bot';

      this.chatHeaderTxt = isBot ? this.webchatSettings.chatHeaderTxt : message.getAgentName(msg, isHistory);

      // if msg src is bot, show operator as defined in database. If msg src is agent, get his name.
      const agentName = isBot ? this.webchatSettings.operator : message.getAgentName(msg, isHistory);
      // update message.from field by this.agentNam
      message.updateMessageSender(msg, agentName);

      // if msg src is bot, get operator image from database. If msg src is agent, get his profile photo.
      this.agentImg = (isBot ? this.webchatSettings.operatorImage : message.getAgentImage(msg, isHistory)) || this.webchatSettings.operatorImage;
    },
    getProfilelangs(langs: ProfileLangs) {
      this.langs = langs;

      this.$i18n.mergeLocaleMessage(this.$i18n.locale, { captureLead: langs });
    },
    async identifyUser(user: WebchatUser, payload: any) {
      if (!this.messages) {
        await window.botClient.getHistory();
      }
      window.botClient.setUserIdentity(user, payload.PAYLOAD, payload.actionPayload);
      this.enableWebviewCaptureLead = false;
      setTimeout(() => {
        this.loadCaptureLeadWebview = false;
      }, 5000);
    },
    isLanguageSelectorEnabled(loadUserLangSelector: boolean) {
      this.loadLanguageSelector = loadUserLangSelector;
    },
    isCaptureLeadEnabled(enableWebview: boolean) {
      this.loadCaptureLeadWebview = enableWebview;
    },
    manageMobileTheme(activateMobileTheme: boolean) {
      this.isFullScreen = activateMobileTheme;
      console.log('In vue mobile theme, full screen is ', activateMobileTheme);
    },
    manageStandaloneMode(activateStandaloneMode: boolean, closeButton: boolean) {
      if (activateStandaloneMode) {
        window.open(window.location.origin + "/webchat/chatChannel.html#?profileId=" + this.profileId + "&token=" + this.token + '&channelId=' + this.channelId + "&voiceMode=false&lang=" + this.profileDefaultLang + "closeWebchat=false&closeButton=" + closeButton);
      }
    },
    notifyCount(count: number) {
      window.botClient.notifyParent({
        name: 'unreadCount',
        unreadCount: count
      });
    },
    resetUnreadCount() {
      if (this.messages.length) {
        message.setunreadMessagesTimestamp(this.messages[this.messages.length - 1].timestamp);
        this.notifyCount(0);
      }
    },
    setLocale(selectedLanguage: string) {

      if (selectedLanguage == 'ar') {
        this.$i18n.locale = 'arabic';
      } else if (selectedLanguage == 'da') {
        this.$i18n.locale = 'danish';
      } else if (selectedLanguage == 'nl') {
        this.$i18n.locale = 'dutch';
      }
      else {
        this.$i18n.locale = 'english';
      }

      this.selectedLang = selectedLanguage;

    },
    async selectLang(lang: any) {
      this.userPreferredLang = lang ? lang : '';

      if (lang) {
        await window.botClient.setUserPreferredLanguage({ preferred_language: this.userPreferredLang });
        this.setLocale(this.userPreferredLang);
      }

      this.openSelectLang = false;
      this.loadLanguageSelector = false;
    },
    setOpenSelectLangView(open: boolean) {
      this.openSelectLang = open;
      this.addBackBtn = true;
    },
    backToChat() {
      this.openSelectLang = false;
      this.closeSelectLang = true;
    },
    showChatWindow() {
      if (!this.loadCaptureLeadWebview) return true;
      return (this.loadLanguageSelector || this.openSelectLang);
    },
    onFileInputChange(files: any) {
      this.attachmentsFiles = files;
    },
    onToggleBubble(flag: any) {
      this.toggleWindowBubble = flag;
    },
    async updateLang(lang: string) {
      console.log("updateLang from Quick replies to ", lang);

      this.userPreferredLang = lang ? lang : null;

      if (lang) {
        await window.botClient.setUserPreferredLanguage({ preferred_language: this.userPreferredLang });
        this.setLocale(this.userPreferredLang);
      }
    },
  },
  beforeDestroy() {
    window.botClient.closeWebsocket();
  }
});
export default webChat;
